import { NavLink, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';

import { useUiStore } from '@/store/uiStore';
import { useMe } from '@/hooks/useAuth';
import { useAdminUsers, type AdminUserRow } from '@/api/adminUsers';
import styles from './Sidebar.module.css';

// ── Inline SVG icons (no external icon dep) ─────────────────────────────────
function DashboardIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="3" width="6" height="6" rx="1.5" />
      <rect x="11" y="3" width="6" height="6" rx="1.5" />
      <rect x="3" y="11" width="6" height="6" rx="1.5" />
      <rect x="11" y="11" width="6" height="6" rx="1.5" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="8" cy="7" r="3" />
      <path d="M2 17c0-3.3 2.7-6 6-6s6 2.7 6 6" strokeLinecap="round" />
      <circle cx="14.5" cy="6" r="2" />
      <path d="M14.5 11c2.5 0 4.5 2 4.5 4.5" strokeLinecap="round" />
    </svg>
  );
}

function WorkoutsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M2 10h2M16 10h2" strokeLinecap="round" />
      <rect x="4" y="7" width="3" height="6" rx="1" />
      <rect x="13" y="7" width="3" height="6" rx="1" />
      <path d="M7 10h6" strokeLinecap="round" strokeWidth="2" />
    </svg>
  );
}

// Stacked cards — the library of programs, distinct from the single
// Program page's document-like icon.
function ProgramsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2.5" y="6.5" width="11" height="11" rx="1.5" />
      <path d="M6 3.5h9A1.5 1.5 0 0 1 16.5 5v9" strokeLinecap="round" />
      <path d="M5.5 10h5M5.5 13h3" strokeLinecap="round" />
    </svg>
  );
}

// Cloud with a down-arrow — Drive sync.
function DriveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M5.5 15a3.5 3.5 0 0 1-.3-6.99 4.5 4.5 0 0 1 8.7-1.4A3.75 3.75 0 0 1 15 15H5.5Z" />
      <path d="M10 9.5v4.5m0 0 1.75-1.75M10 14l-1.75-1.75" strokeLinecap="round" />
    </svg>
  );
}

function ExercisesIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path
        d="M6 6 C6 4 8 3 10 4 C11 4.5 11.5 5.5 12 6 C12 5.5 12.5 4.5 13.5 4 C15 3 17 4 17 6 C17 11 13 13 10 13 C7 13 3 11 3 6"
        strokeLinejoin="round"
      />
      <path d="M3 17h14" strokeLinecap="round" />
    </svg>
  );
}

function EquipmentIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2" y="8" width="2" height="4" rx="0.6" />
      <rect x="16" y="8" width="2" height="4" rx="0.6" />
      <rect x="4" y="7" width="2" height="6" rx="0.6" />
      <rect x="14" y="7" width="2" height="6" rx="0.6" />
      <path d="M6 10h8" strokeLinecap="round" strokeWidth="2.2" />
    </svg>
  );
}

function PlanLabIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path
        d="M7 3v4l-3.5 7a1.5 1.5 0 001.4 2.1h10.2A1.5 1.5 0 0016.5 14L13 7V3"
        strokeLinejoin="round"
      />
      <path d="M6 3h8" strokeLinecap="round" />
      <path d="M5.5 12h9" strokeLinecap="round" />
    </svg>
  );
}

function JourneyLabIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="4" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="16" cy="10" r="1.4" />
      <path d="M5.4 10h3.2M11.4 10h3.2" strokeLinecap="round" />
      <path d="M3 4h14M3 16h14" strokeLinecap="round" strokeOpacity="0.4" />
    </svg>
  );
}

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
}

const MANAGE_NAV_ITEMS: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/users', label: 'Users', icon: <UsersIcon /> },
  // Program / Programs / Workouts were three nav items over the same
  // content. Now one: /programs, with Library and Curation tabs.
  { to: '/programs', label: 'Programs', icon: <ProgramsIcon /> },
  { to: '/drive', label: 'Drive', icon: <DriveIcon /> },
  { to: '/activity', label: 'Activity', icon: <WorkoutsIcon /> },
  { to: '/exercises', label: 'Exercises', icon: <ExercisesIcon /> },
  { to: '/equipment', label: 'Equipment', icon: <EquipmentIcon /> },
];

const DEVELOPER_NAV_ITEMS: NavEntry[] = [
  { to: '/admin/engine-v2/plan-lab', label: 'Engine V2 Plan Lab', icon: <PlanLabIcon /> },
  {
    to: '/admin/engine-v2/user-journey-lab',
    label: 'Engine V2 User Journey Lab',
    icon: <JourneyLabIcon />,
  },
  {
    to: '/admin/engine-v2/audit-matrix',
    label: 'Engine V2 Audit Matrix',
    icon: <PlanLabIcon />,
  },
];

function VitalLibraryIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2.5" y="5.5" width="11" height="9" rx="1.5" />
      <path d="M13.5 8.5L17.5 6.5v7l-4-2z" strokeLinejoin="round" />
      <path d="M5 3.5h7" strokeLinecap="round" />
    </svg>
  );
}

// Clapperboard with a sparkle — AI-generated renders.
function AiRendersIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2.5" y="7" width="12" height="8.5" rx="1.5" />
      <path d="M2.5 7l2-3h10l-2 3" strokeLinejoin="round" />
      <path d="M16.5 9.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6z" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="5" width="14" height="12" rx="1.5" />
      <path d="M3 8h14" />
      <path d="M7 3v3M13 3v3" strokeLinecap="round" />
    </svg>
  );
}

// ── Brand mark ──────────────────────────────────────────────────────────────
function BrandMark() {
  return (
    <img src="/logo.PNG" alt="" width={28} height={28} className="dc-brand-mark" decoding="async" />
  );
}

// ── User plans section ──────────────────────────────────────────────────────
//
// Flat list of users sourced from `useAdminUsers`. Each row is a single
// NavLink straight to that user's plan editor — date drill-down lives
// on the editor page itself (the 14-day rail there is the canonical
// surface for picking a date + workout).

function nameFor(row: AdminUserRow): string {
  const parts = [row.firstName, row.lastName].filter((s): s is string => s !== null && s !== '');
  if (parts.length > 0) return parts.join(' ');
  return row.email;
}

function UserPlansSection({ collapsed }: { collapsed: boolean }) {
  const usersQuery = useAdminUsers();
  const location = useLocation();

  // Match the current plan-editor route so the active user's row gets
  // the same highlight as a top-level nav entry.
  const planEditorMatch = /^\/users\/([0-9a-f-]+)\/plan-editor/i.exec(location.pathname);
  const activeUserId = planEditorMatch?.[1] ?? null;

  if (collapsed) {
    // Collapsed sidebar has no room for a flat user list; render a
    // single icon link to the Users index instead.
    return (
      <NavLink
        to="/users"
        className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
        title="User plans"
      >
        <span className={styles.navIcon} aria-hidden="true">
          <CalendarIcon />
        </span>
      </NavLink>
    );
  }

  if (usersQuery.isLoading) {
    return <div className={styles.userDayEmpty}>Loading users…</div>;
  }
  if (usersQuery.isError) {
    return <div className={styles.userDayEmpty}>Failed to load users.</div>;
  }
  const users = usersQuery.data?.users ?? [];
  if (users.length === 0) {
    return <div className={styles.userDayEmpty}>No users yet.</div>;
  }

  return (
    <div className={styles.userPlansList}>
      {users.map((u) => (
        <NavLink
          key={u.id}
          to={`/users/${u.id}/plan-editor`}
          className={`${styles.userPlanLink} ${activeUserId === u.id ? styles.userPlanLinkActive : ''}`}
          title={u.email}
        >
          <span className={styles.userPlanIcon} aria-hidden="true">
            <CalendarIcon />
          </span>
          <span className={styles.userPlanName}>{nameFor(u)}</span>
        </NavLink>
      ))}
    </div>
  );
}

function NavItem({ entry, collapsed }: { entry: NavEntry; collapsed: boolean }) {
  return (
    <NavLink
      to={entry.to}
      className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
      title={collapsed ? entry.label : undefined}
    >
      <span className={styles.navIcon} aria-hidden="true">
        {entry.icon}
      </span>
      {!collapsed && <span className={styles.navLabelText}>{entry.label}</span>}
    </NavLink>
  );
}

const VITAL_LIBRARY_NAV_ITEM: NavEntry = {
  to: '/admin/vital-library',
  label: 'Vital library',
  icon: <VitalLibraryIcon />,
};

// Two entries, because they are two jobs: getting the motion right in
// Blender, and restyling it in Higgsfield. A task lives on exactly one of
// them at a time.
const BLENDER_NAV_ITEM: NavEntry = {
  to: '/render-pipeline',
  label: 'Blender renders',
  icon: <AiRendersIcon />,
};

const HIGGSFIELD_NAV_ITEM: NavEntry = {
  to: '/render-pipeline/higgsfield',
  label: 'Higgsfield',
  icon: <AiRendersIcon />,
};

export const Sidebar = () => {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const { data: me, isLoading: meLoading } = useMe();
  // Nav gating:
  //   • Admins see all four sections (Manage / User plans / Developer /
  //     Video pipeline).
  //   • Everyone else sees the "no access" notice.
  //
  // `isVideoEditor` no longer grants anything. It used to open the Video
  // editing entry, which was the only page a non-admin editor could reach;
  // that page and its endpoints are deleted, so gating the section on the
  // flag would now render a section header with nothing under it — every
  // remaining entry is admin-only. Falling through to the no-access notice
  // is the honest outcome. The flag itself still exists on the user record.
  const isAdmin = me?.isAdmin === true;
  const showAdminSections = isAdmin;
  const showVideoPipelineSection = isAdmin;

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.brand} title="LIFTIQ">
        <BrandMark />
        {!collapsed && <span className={styles.brandText}>LIFTIQ</span>}
      </div>

      <nav className={styles.nav}>
        {showAdminSections ? (
          <>
            {!collapsed && <div className={styles.navLabel}>Manage</div>}
            {MANAGE_NAV_ITEMS.map((entry) => (
              <NavItem key={entry.to} entry={entry} collapsed={collapsed} />
            ))}

            {!collapsed && <div className={styles.navLabel}>User plans</div>}
            <UserPlansSection collapsed={collapsed} />

            {!collapsed && <div className={styles.navLabel}>Developer</div>}
            {DEVELOPER_NAV_ITEMS.map((entry) => (
              <NavItem key={entry.to} entry={entry} collapsed={collapsed} />
            ))}
          </>
        ) : null}

        {showVideoPipelineSection && (
          <>
            {/* Renamed from "Video editing": the hand-editing pages are gone
                and what is left is the sourcing-and-generation pipeline —
                licensed clips, then Blender, then Higgsfield. */}
            {!collapsed && <div className={styles.navLabel}>Video pipeline</div>}
            <NavItem entry={VITAL_LIBRARY_NAV_ITEM} collapsed={collapsed} />
            <NavItem entry={BLENDER_NAV_ITEM} collapsed={collapsed} />
            <NavItem entry={HIGGSFIELD_NAV_ITEM} collapsed={collapsed} />
          </>
        )}

        {!showAdminSections && !showVideoPipelineSection && !meLoading && !collapsed && (
          <div className={styles.userDayEmpty}>Admin access required.</div>
        )}
      </nav>

      {!collapsed && (
        <div className={styles.footer}>
          <span className={styles.footerVersion}>v0.1.0</span>
        </div>
      )}
    </aside>
  );
};
